import { useState, useEffect, useCallback } from 'react'
import {
  getTutorial,
  setTutorial,
} from '@sportninja/common/utils/TutorialHelper'
import { tutorialKeys } from '@sportninja/common/constants/userGuiding'
import { isCanlan } from '@sportninja/common/utils/customer-name'

// Session storage keys for trigger conditions
const TRIGGER_KEYS = {
  EMAIL_VERIFIED: 'onboarding_trigger_email_verified',
  ACCOUNT_UPGRADED: 'onboarding_trigger_account_upgraded',
}

export type OnboardingStatus =
  | 'not_started'
  | 'skipped'
  | 'in_progress'
  | 'completed'

interface MeUser {
  id?: number
  account_id?: number
  email?: string
}

interface UseOnboardingTutorialProps {
  me: MeUser | null
  isLoggedIn: boolean
}

interface UseOnboardingTutorialReturn {
  shouldShowModal: boolean
  shouldShowBanner: boolean
  tutorialStatus: OnboardingStatus | null
  isLoading: boolean
  error: string | null
  updateTutorialStatus: (status: OnboardingStatus) => Promise<void>
  openModal: () => void
  closeModal: () => void
  isModalOpen: boolean
}

/**
 * Custom hook to manage the onboarding wizard tutorial state
 * Handles API integration, conditions checking, and debug mode
 */
export const useOnboardingTutorial = ({
  me,
  isLoggedIn,
}: UseOnboardingTutorialProps): UseOnboardingTutorialReturn => {
  const [tutorialStatus, setTutorialStatus] = useState<OnboardingStatus | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDebugMode, setIsDebugMode] = useState(false)

  // Check if conditions are met to show tutorial
  const shouldRun = me && me?.account_id && !isCanlan && isLoggedIn

  // Check if user has @sportninja.com email for debug mode
  const isDebugUser = me?.email?.endsWith('@sportninja.com')

  /**
   * Check for trigger conditions from session storage
   */
  const checkTriggerConditions = useCallback(() => {
    try {
      const emailVerifiedTrigger = sessionStorage.getItem(
        TRIGGER_KEYS.EMAIL_VERIFIED
      )
      const accountUpgradedTrigger = sessionStorage.getItem(
        TRIGGER_KEYS.ACCOUNT_UPGRADED
      )

      return (
        emailVerifiedTrigger === 'true' || accountUpgradedTrigger === 'true'
      )
    } catch (error) {
      return false
    }
  }, [])

  /**
   * Clear all trigger conditions from session storage
   */
  const clearTriggerConditions = useCallback(() => {
    try {
      sessionStorage.removeItem(TRIGGER_KEYS.EMAIL_VERIFIED)
      sessionStorage.removeItem(TRIGGER_KEYS.ACCOUNT_UPGRADED)
    } catch (error) {
      // Silent fail
    }
  }, [])

  /**
   * Fetch tutorial status from API
   */
  const fetchTutorialStatus = useCallback(async () => {
    try {
      setIsLoading(true)
      const result = await getTutorial(tutorialKeys.ONBOARDING_WIZARD_STATUS)

      if (result?.tutorialStep?.value) {
        const status = result.tutorialStep.value as OnboardingStatus
        setTutorialStatus(status)
      } else {
        setTutorialStatus('not_started')
      }
    } catch (err) {
      setError('Failed to fetch tutorial status')
      setTutorialStatus('not_started')
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Update tutorial status in API
   */
  const updateTutorialStatus = useCallback(async (status: OnboardingStatus) => {
    try {
      await setTutorial(tutorialKeys.ONBOARDING_WIZARD_STATUS, status)
      setTutorialStatus(status)

      // Close modal if completed
      if (status === 'completed') {
        setIsModalOpen(false)
        setIsDebugMode(false)
      }
    } catch (err) {
      setError('Failed to update tutorial status')
      throw err
    }
  }, [])

  /**
   * Open the modal manually (e.g., from banner or debug mode)
   */
  const openModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  /**
   * Close the modal
   */
  const closeModal = useCallback(() => {
    setIsModalOpen(false)
    // Also reset debug mode when closing
    setIsDebugMode(false)
  }, [])

  /**
   * Set up keyboard listener for debug mode (Ctrl+T)
   */
  useEffect(() => {
    if (!isDebugUser) {
      return
    }

    const handleKeyPress = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 't') {
        event.preventDefault()
        setIsDebugMode(true)
        setIsModalOpen(true)
      }
    }

    const cleanup = () => {
      window.removeEventListener('keydown', handleKeyPress)
    }

    window.addEventListener('keydown', handleKeyPress)

    // Return cleanup function to prevent memory leaks
    return cleanup
  }, [isDebugUser])

  /**
   * Fetch tutorial status on mount if conditions are met
   */
  useEffect(() => {
    if (shouldRun) {
      fetchTutorialStatus()
    }
  }, [shouldRun, fetchTutorialStatus])

  /**
   * Determine if modal should be shown - ONLY for specific trigger conditions
   */
  useEffect(() => {
    if (!isLoading && shouldRun && !isDebugMode) {
      const hasTrigger = checkTriggerConditions()

      // Show modal ONLY when there are specific triggers (email verification or account upgrade)
      if (hasTrigger && tutorialStatus !== 'completed') {
        setIsModalOpen(true)
        clearTriggerConditions()

        // If user hasn't started the tutorial yet, mark as in_progress
        if (tutorialStatus === 'not_started') {
          updateTutorialStatus('in_progress')
        }
      }
    }
  }, [
    isLoading,
    shouldRun,
    tutorialStatus,
    isDebugMode,
    updateTutorialStatus,
    checkTriggerConditions,
    clearTriggerConditions,
  ])

  // Determine what should be shown
  const shouldShowModal = isModalOpen
  const shouldShowBanner = tutorialStatus === 'skipped' && !isModalOpen

  return {
    shouldShowModal,
    shouldShowBanner,
    tutorialStatus,
    isLoading,
    error,
    updateTutorialStatus,
    openModal,
    closeModal,
    isModalOpen,
  }
}

// Export trigger keys for use in other components
export { TRIGGER_KEYS }
